import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOrganization,
  selectOrganization,
  selectIsLoading,
  selectError,
  selectIsUserLeader
} from '../../redux/organization/organizationSlice';
import { AppDispatch, RootState } from '../../redux/store';
import { MemberInviteForm } from '../../components/organization/MemberInviteForm';
import { MemberList } from '../../components/organization/MemberList';
import Sidebar from '../../components/sidebar/Sidebar';
import { useNavigate } from 'react-router-dom';
import { useLogout } from '../../hooks/useLogout';
import { Building2, User, Users } from 'lucide-react';
import {
  TaskyButton,
  TaskyEmptyState,
  TaskyErrorState,
  ResponsiveAppShell,
  TaskySpinner,
  TaskyTopBar,
} from '../../components/tasky';

// 円形スコアリング（見た目のみ）: r=42 の円周
const SCORE_RING_CIRCUMFERENCE = 263.9;

const scoreRingOffset = (score: number): number => {
  const pct = Math.min(100, Math.max(0, score));
  return SCORE_RING_CIRCUMFERENCE * (1 - pct / 100);
};

export const OrganizationPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const organization = useSelector(selectOrganization);
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectError);
  const isLeader = useSelector(selectIsUserLeader);

  // ユーザー情報とチャット履歴を取得
  const { user } = useSelector((state: RootState) => state.auth);
  const { chats } = useSelector((state: RootState) => state.chat);

  // ログアウト処理
  const logout = useLogout();

  // サイドバーの開閉状態
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  // 組織情報を取得
  useEffect(() => {
    dispatch(fetchOrganization());
  }, [dispatch, refreshKey]);

  // メンバーリスト更新時のハンドラ
  const handleMemberUpdate = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  // 組織ページのコンテンツをレンダリングする関数
  const renderContent = () => {
    if (isLoading && !organization) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-warm-bg">
          <TaskySpinner size="lg" />
          <p className="text-sm text-gray-500">組織情報を読み込み中...</p>
        </div>
      );
    }

    if (error && !organization) {
      return (
        <div className="flex-1 bg-warm-bg p-4 sm:p-6">
          <div className="max-w-xl mx-auto mt-12">
            <TaskyErrorState
              title="エラーが発生しました"
              description={error}
              action={
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={() => dispatch(fetchOrganization())}
                >
                  再読込
                </TaskyButton>
              }
            />
          </div>
        </div>
      );
    }

    if (!organization) {
      return (
        <div className="flex-1 bg-warm-bg flex items-center justify-center p-6">
          <TaskyEmptyState
            icon={<Building2 />}
            title="組織に所属していません"
            description="組織に招待されるか、新しい組織を作成してください。"
          />
        </div>
      );
    }

    return (
      <div className="flex min-h-[calc(100dvh-56px)] flex-col lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title={organization.name}
          icon={<Building2 className="w-5 h-5" />}
          titleAddon={
            organization.description ? (
              <span className="text-sm text-gray-500 truncate">{organization.description}</span>
            ) : undefined
          }
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-warm-bg">
          <div className="max-w-5xl mx-auto px-4 py-5 space-y-5 sm:px-6 sm:py-6 sm:space-y-6">
            {/* AIスコア情報表示 - リーダーのみ全体表示、メンバーは自分のだけ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-tutorial="organization-score">
              {isLeader && (
                <div className="bg-white rounded-tasky-md p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">組織全体</p>
                      <p className="text-xs text-gray-400">AI活用スコア</p>
                      <p className="text-4xl font-bold text-dark mt-3">{organization.ai_usage_score || 0}</p>
                      <p className="text-xs text-gray-400 mt-1">/ 100</p>
                    </div>
                    <div className="relative">
                      <svg className="-rotate-90" width="100" height="100" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" strokeLinecap="round" stroke="#E8E6E0" />
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          fill="none"
                          strokeWidth="8"
                          strokeLinecap="round"
                          stroke="#616FC9"
                          strokeDasharray={SCORE_RING_CIRCUMFERENCE}
                          strokeDashoffset={scoreRingOffset(organization.ai_usage_score || 0)}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Users className="w-7 h-7 text-primary" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-tasky-md p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">あなたのスコア</p>
                    <p className="text-xs text-gray-400">AI活用スコア</p>
                    <p className="text-4xl font-bold text-dark mt-3">{user?.ai_usage_score || 0}</p>
                    <p className="text-xs text-gray-400 mt-1">/ 100</p>
                  </div>
                  <div className="relative">
                    <svg className="-rotate-90" width="100" height="100" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" strokeLinecap="round" stroke="#E8E6E0" />
                      <circle
                        cx="50"
                        cy="50"
                        r="42"
                        fill="none"
                        strokeWidth="8"
                        strokeLinecap="round"
                        stroke="#9675C9"
                        strokeDasharray={SCORE_RING_CIRCUMFERENCE}
                        strokeDashoffset={scoreRingOffset(user?.ai_usage_score || 0)}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <User className="w-7 h-7 text-purple" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* メンバー招待フォーム - リーダーのみ表示 */}
            <MemberInviteForm onSuccess={handleMemberUpdate} />

            {/* メンバーリスト */}
            <MemberList onMemberUpdate={handleMemberUpdate} />
          </div>
        </div>
      </div>
    );
  };

  const sidebar = (
    <Sidebar
      onLogout={logout}
      chats={chats || []}
      isLeader={isLeader}
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = (
    <Sidebar
      onLogout={logout}
      chats={chats || []}
      isLeader={isLeader}
    />
  );

  return (
    <ResponsiveAppShell
      sidebar={sidebar}
      mobileSidebar={mobileSidebar}
      topbarTitle={organization?.name || '組織'}
    >
      {renderContent()}
    </ResponsiveAppShell>
  );
};
